import React from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter, Link } from 'react-router-dom';
import marked from 'marked';
import styled from 'styled-components';
import {
    BodyContainer,
    Container,
    Image,
} from '../../components/StyledComponents/StyledComponents';
import Navigation from '../../components/Navigation/Navigation.jsx';
import Footer from '../../components/Footer/Footer.jsx';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import SEO from '../../components/SEO/SEO.jsx';
import Author from '../../components/Author/Author.jsx';
import {
    getPosts,
    getCategoryNameByPath,
    getCategoryPathFromLeaf,
} from '../../posts/Posts';
import {
    extractMetadata,
    generateArticleSchema,
    generateFAQSchema,
    getCanonicalUrl,
} from '../../utils/postMetadata';
import { supportedLanguages } from '../../i18n';

// Configure marked renderer to allow raw HTML
const renderer = new marked.Renderer();

// Override html renderer to pass HTML through without escaping
renderer.html = function (html) {
    return html;
};

// Override paragraph renderer to check for HTML tags and not wrap them
const originalParagraph = renderer.paragraph.bind(renderer);
renderer.paragraph = function (text) {
    // If text starts with an HTML tag, return it as-is without wrapping in <p>
    if (text.trim().startsWith('<')) {
        return text + '\n';
    }
    return originalParagraph(text);
};

// Configure marked options
marked.setOptions({
    renderer: renderer,
    gfm: true,
    breaks: false,
    pedantic: false,
    sanitize: false,
    smartLists: true,
    smartypants: false,
});

const BreadcrumbContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin: 1.5rem 0;
    font-size: 14px;
    color: ${(props) => props.theme.body.text};
`;

const BreadcrumbItem = styled(Link)`
    color: ${(props) => props.theme.body.text};
    opacity: ${(props) => (props.isLast ? 1 : 0.8)};
    font-weight: ${(props) => (props.isLast ? 500 : 400)};
    text-decoration: none;
    transition: ${(props) => props.theme.transition};

    &:hover {
        opacity: 1;
        color: ${(props) => props.theme.colors.primary};
    }
`;

const BreadcrumbSeparator = styled.span`
    margin: 0 0.5rem;
    color: ${(props) => props.theme.body.text};
    opacity: 0.5;
`;

const MarkdownContent = styled.div`
    line-height: 1.8;
    color: ${(props) => props.theme.body.text};

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        color: ${(props) => props.theme.body.headers};
        margin-top: 2rem;
        margin-bottom: 1rem;
        font-weight: 600;
    }

    p {
        margin-bottom: 1.5rem;
    }

    img {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 2rem auto;
        border-radius: 4px;
    }

    ul,
    ol {
        margin-bottom: 1.5rem;
        padding-left: 2rem;
    }

    li {
        margin-bottom: 0.5rem;
    }

    a {
        color: ${(props) => props.theme.colors.primary};
        text-decoration: underline;
        font-weight: 500;
        transition: all 0.2s ease;

        &:hover {
            color: ${(props) => props.theme.colors.primary};
            opacity: 0.8;
            text-decoration: underline;
        }
    }

    blockquote {
        border-left: 4px solid ${(props) => props.theme.colors.primary};
        padding-left: 1.5rem;
        margin: 1.5rem 0;
        font-style: italic;
        color: ${(props) => props.theme.body.text};
        opacity: 0.8;
    }

    code {
        background-color: ${(props) =>
            props.theme.mode === 'light' ? '#f5f5f5' : '#2a2a2a'};
        padding: 0.2rem 0.4rem;
        border-radius: 3px;
        font-size: 0.9em;
    }

    pre {
        background-color: ${(props) =>
            props.theme.mode === 'light' ? '#f5f5f5' : '#2a2a2a'};
        padding: 1rem;
        border-radius: 4px;
        overflow-x: auto;
        margin: 1.5rem 0;

        code {
            background-color: transparent;
            padding: 0;
        }
    }
`;

class BlogPost extends React.Component {
    state = {
        markdown: '',
        post: null,
        loading: true,
        error: null,
        metadata: null,
    };

    componentDidMount() {
        this.loadPost();

        // Listen for language changes
        const { i18n } = this.props;
        if (i18n) {
            this.languageChangeHandler = (_) => {
                this.loadPost();
            };
            i18n.on('languageChanged', this.languageChangeHandler);
        }
    }

    componentWillUnmount() {
        // Clean up language change listener
        const { i18n } = this.props;
        if (i18n && this.languageChangeHandler) {
            i18n.off('languageChanged', this.languageChangeHandler);
        }
    }

    componentDidUpdate(prevProps) {
        // Reload post if route changed
        const routeChanged = prevProps.match?.url !== this.props.match?.url;

        if (routeChanged) {
            this.loadPost();
        }
    }

    processMarkdown = (text) => {
        const metadata = extractMetadata(text);

        let processedText = text;
        const frontmatterRegex = /^---\s*\n[\s\S]*?\n---\s*\n/;
        processedText = processedText.replace(frontmatterRegex, '');

        const publicUrl = process.env.PUBLIC_URL || '';
        processedText = processedText.replace(
            /!\[([^\]]*)\]\(([^)]+)\)/g,
            (match, alt, src) => {
                if (src.startsWith('http://') || src.startsWith('https://')) {
                    return match;
                }
                if (src.startsWith('/img/')) {
                    return `![${alt}](${publicUrl}${src})`;
                }
                let cleanPath = src.replace(/^(\.\.\/)+public\//, '');
                cleanPath = cleanPath.replace(/^\.\.\//, '');
                if (!cleanPath.startsWith('/')) {
                    cleanPath = `/img/${cleanPath}`;
                }
                return `![${alt}](${publicUrl}${cleanPath})`;
            }
        );

        processedText = processedText.replace(/(<img[\s\S]*?>)/g, '\n\n$1\n\n');
        const markedOutput = marked(processedText);
        this.setState({
            markdown: markedOutput,
            metadata: metadata,
            loading: false,
            error: null,
        });
    };

    loadPost = () => {
        const { i18n, match, location } = this.props;

        const languageFromUrl = match?.params?.lang;
        const language =
            (languageFromUrl && supportedLanguages.includes(languageFromUrl)
                ? languageFromUrl
                : i18n?.language) ||
            localStorage.getItem('i18nextLng') ||
            'en';

        if (i18n && i18n.language !== language) {
            i18n.changeLanguage(language);
        }

        let route = '';
        if (match?.url) {
            route = match.url.startsWith('/')
                ? match.url.substring(1)
                : match.url;
        } else if (location?.pathname) {
            route = location.pathname.startsWith('/')
                ? location.pathname.substring(1)
                : location.pathname;
        } else {
            route = window.location.pathname.startsWith('/')
                ? window.location.pathname.substring(1)
                : window.location.pathname;
        }

        const pathSegments = route.split('/').filter(Boolean);
        if (pathSegments[0] === 'blog') pathSegments.shift();
        if (
            pathSegments.length > 0 &&
            supportedLanguages.includes(pathSegments[0])
        )
            pathSegments.shift();
        if (pathSegments[0] === 'posts') pathSegments.shift();

        route = pathSegments.join('/');

        this.setState({
            post: null,
            error: null,
        });

        const posts = getPosts(language);
        const post = posts.find((p) => p.route === route);

        if (!post) {
            console.error(
                'Post not found for route:',
                route,
                'language:',
                language
            );
            this.setState({
                loading: false,
                error: 'Post not found',
                post: null,
            });
            return;
        }

        this.setState({ post });

        if (
            window.__INITIAL_POST_DATA__ &&
            window.__INITIAL_POST_DATA__.route === route &&
            window.__INITIAL_POST_DATA__.language === language
        ) {
            this.processMarkdown(window.__INITIAL_POST_DATA__.content);
            return;
        }

        this.setState({ loading: true });

        fetch(post.content)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Failed to load post: ${response.statusText}`
                    );
                }
                return response.text();
            })
            .then((text) => {
                // Store in global variable to avoid re-fetching
                window.__INITIAL_POST_DATA__ = {
                    route: route,
                    language: language,
                    content: text,
                };
                this.processMarkdown(text);
            })
            .catch((error) => {
                console.error('Error loading post content:', error);
                this.setState({
                    loading: false,
                    error: error.message,
                    markdown: '',
                });
            });
    };

    render() {
        const { markdown, post, loading, error, metadata } = this.state;
        const { i18n } = this.props;
        const language = i18n?.language || 'en';

        // Get all category paths for breadcrumbs (reconstruct from leaf for each category)
        const categoryPaths =
            post?.categories && post.categories.length > 0
                ? post.categories
                      .map((categoryId) => getCategoryPathFromLeaf(categoryId))
                      .filter((path) => path && path.length > 0)
                : [];

        // Generate SEO data
        const seoTitle = metadata?.title || post?.title;
        const seoDescription = metadata?.metaDescription || post?.description;
        const seoImage = post?.image;
        const seoUrl = post ? getCanonicalUrl(post.route, language) : null;
        const seoKeywords = metadata?.keywords || '';
        const articleSchema =
            post && metadata
                ? generateArticleSchema(post, metadata, seoUrl, language)
                : null;
        const faqSchema = metadata ? generateFAQSchema(metadata) : null;

        return (
            <Container>
                {post && (
                    <SEO
                        title={seoTitle}
                        description={seoDescription}
                        image={seoImage}
                        url={seoUrl}
                        type="article"
                        keywords={seoKeywords}
                        datePublished={metadata?.datePublished}
                        dateModified={metadata?.dateModified}
                        author={metadata?.author}
                        schema={articleSchema}
                        faqSchema={faqSchema}
                        lang={language}
                    />
                )}
                <ProgressBar />
                <Navigation />
                <BodyContainer>
                    <Container small top={12}>
                        {loading && <p>Loading...</p>}
                        {error && <p>Error: {error}</p>}
                        {!loading && !error && post && (
                            <>
                                <h1>{post.title}</h1>
                                <p>{post.date}</p>

                                {categoryPaths.length > 0 && (
                                    <>
                                        {categoryPaths.map(
                                            (categoryPath, categoryIndex) => (
                                                <BreadcrumbContainer
                                                    key={categoryIndex}
                                                >
                                                    {categoryPath.map(
                                                        (categoryId, index) => {
                                                            const pathToCategory =
                                                                categoryPath.slice(
                                                                    0,
                                                                    index + 1
                                                                );
                                                            const categoryName =
                                                                getCategoryNameByPath(
                                                                    pathToCategory,
                                                                    language
                                                                );
                                                            const isLast =
                                                                index ===
                                                                categoryPath.length -
                                                                    1;
                                                            // Create URL with category path as query parameter
                                                            const categoryPathParam =
                                                                pathToCategory.join(
                                                                    ','
                                                                );
                                                            const postsUrl = `/${language}/posts?category=${encodeURIComponent(
                                                                categoryPathParam
                                                            )}`;

                                                            return (
                                                                <React.Fragment
                                                                    key={index}
                                                                >
                                                                    <BreadcrumbItem
                                                                        to={
                                                                            postsUrl
                                                                        }
                                                                        isLast={
                                                                            isLast
                                                                        }
                                                                    >
                                                                        {
                                                                            categoryName
                                                                        }
                                                                    </BreadcrumbItem>
                                                                    {!isLast && (
                                                                        <BreadcrumbSeparator>
                                                                            ›
                                                                        </BreadcrumbSeparator>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        }
                                                    )}
                                                </BreadcrumbContainer>
                                            )
                                        )}
                                    </>
                                )}

                                <Image
                                    src={post.image}
                                    alt={post.title || 'Imagen del artículo'}
                                    width={800}
                                    height={450}
                                    loading="eager"
                                    fetchpriority="high"
                                    style={{
                                        maxWidth: '100%',
                                        height: 'auto',
                                        aspectRatio: '800 / 450',
                                    }}
                                />
                                {markdown && (
                                    <MarkdownContent
                                        id={`post_${post.route}`}
                                        dangerouslySetInnerHTML={{
                                            __html: markdown,
                                        }}
                                    />
                                )}

                                {/* Author Box - Pass authorId from metadata or use default */}
                                <Author
                                    authorId={
                                        metadata?.authorId || 'caro-mendez'
                                    }
                                />
                            </>
                        )}
                    </Container>
                </BodyContainer>
                <Footer />
            </Container>
        );
    }
}

// Use both HOCs to get i18n and router props
export default withRouter(withTranslation()(BlogPost));
